import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../../store'
import { SippApi } from '../../../../services/SippExpress'
import { SnackbarActions } from '../../../../store/snackbar'
import { UserActions, UserSelector } from '../../../../store/User'
import { STRIPE_PRO_ANNUAL_URL } from '../../../../constants/stripeLinks'
import { FILTER_MODELS } from '../../../../data/filterModels'

// Brand list kept in sync with backend earthsmart brand enum (SIPP-807).
// The authoritative match + model normalization runs server-side — the
// frontend only needs the brand options for the dropdown.
const EARTHSMART_BRANDS = [
  'Samsung',
  'LG',
  'Frigidaire',
  'GE',
  'Maytag',
  'Whirlpool',
  'Sears/Kenmore',
  'Amana',
  'KitchenAid',
] as const

type Step = 'select' | 'result' | 'research'

const validateEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

export const BrandModelSearch = () => {
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()

  const savedCapture = useSelector(UserSelector.earthsmartCapture)
  const userEmail = useSelector(UserSelector.email)

  const [step, setStep] = useState<Step>(savedCapture ? 'result' : 'select')
  const [selectedBrand, setSelectedBrand] = useState(savedCapture?.brand ?? '')
  const [model, setModel] = useState(savedCapture?.model ?? '')
  const [filterCode, setFilterCode] = useState(savedCapture?.code ?? '')
  const [matchedBrand, setMatchedBrand] = useState(savedCapture?.brand ?? '')
  const [matchedModel, setMatchedModel] = useState(savedCapture?.model ?? '')
  const [submitting, setSubmitting] = useState(false)

  const [researchEmail, setResearchEmail] = useState(userEmail)
  const [researchError, setResearchError] = useState('')
  const [researchSubmitted, setResearchSubmitted] = useState(false)

  // SIPP-838: track whether the user explicitly cleared the matched state via
  // the Edit button. While `userInitiatedEditRef` is true, the auto-restore
  // effect below stays out of the way — otherwise clicking Edit immediately
  // bounced the user back to the result view because the savedCapture
  // selector still held the prior match.
  const userInitiatedEditRef = useRef(false)

  // If the user arrives mid-flow and the saved capture loads after mount,
  // sync the UI into the result state without blowing away in-progress input.
  useEffect(() => {
    if (userInitiatedEditRef.current) return
    if (!savedCapture) return
    if (step !== 'select') return
    if (selectedBrand || model) return
    setStep('result')
    setSelectedBrand(savedCapture.brand)
    setModel(savedCapture.model)
    setFilterCode(savedCapture.code)
    setMatchedBrand(savedCapture.brand)
    setMatchedModel(savedCapture.model)
  }, [savedCapture, step, selectedBrand, model])

  const resetToSelect = () => {
    userInitiatedEditRef.current = true
    setStep('select')
    setSelectedBrand('')
    setModel('')
    setFilterCode('')
    setMatchedBrand('')
    setMatchedModel('')
  }

  const handleCheck = async () => {
    const trimmedBrand = selectedBrand.trim()
    const trimmedModel = model.trim()
    if (!trimmedBrand || !trimmedModel) return

    setSubmitting(true)
    try {
      const response: any = await SippApi.create.matchRefrigerator({
        brand: trimmedBrand,
        model: trimmedModel,
      })
      if (response?.data?.matched) {
        // A successful match should clear the edit-suppression flag so the
        // saved-capture sync effect can take over again on subsequent
        // mounts / data refreshes (SIPP-838).
        userInitiatedEditRef.current = false
        setFilterCode(response.data.code)
        setMatchedBrand(trimmedBrand)
        setMatchedModel(trimmedModel)
        setStep('result')
        // Re-fetch user so selectors see the newly persisted fields.
        if (userEmail) {
          dispatch(UserActions.fetchUserAsync({ email: userEmail }))
        }
      }
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 404) {
        setResearchSubmitted(false)
        setStep('research')
        return
      }
      if (status === 400) {
        const code = err?.response?.data?.error
        dispatch(
          SnackbarActions.showSnackbar({
            message:
              code === 'invalid_brand'
                ? 'That brand isn\'t in our list yet. Try picking another, or let us know and we\'ll research it.'
                : 'Please pick a brand and enter a model before checking.',
            options: { variant: 'error' },
          }),
        )
        return
      }
      dispatch(
        SnackbarActions.showSnackbar({
          message:
            'Couldn\'t check compatibility right now. Check your connection and try again.',
          options: { variant: 'error' },
        }),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleResearchSubmit = () => {
    if (!researchEmail) {
      setResearchError('Email is required')
      return
    }
    if (!validateEmail(researchEmail)) {
      setResearchError('Please enter a valid email address')
      return
    }
    setResearchError('')
    // D3c will persist this via Klaviyo + CIO. For now, confirm via toast.
    dispatch(
      SnackbarActions.showSnackbar({
        message: `We'll research ${selectedBrand} ${model} and email ${researchEmail} when it's supported.`,
        options: { variant: 'success' },
      }),
    )
    setResearchSubmitted(true)
  }

  return (
    <Stack spacing={2}>
      {/* ── Select brand + model ────────────────────────────────────── */}
      <Collapse in={step === 'select'} unmountOnExit>
        <Stack spacing={2}>
          <Typography variant="h5">Find your refrigerator filter</Typography>
          <Typography variant="body2" color="text.secondary">
            Pick your brand and enter your model number — we'll match you to
            the right filter from our catalog.
          </Typography>

          {/* SIPP-839: brand is a filtered Autocomplete from the
              EARTHSMART_BRANDS list; model field is gated on brand selection
              to prevent users from kicking off a backend match without a
              brand. The submit button stays visible (disabled) so the next
              step is discoverable. */}
          <Autocomplete
            options={[...EARTHSMART_BRANDS]}
            value={selectedBrand || null}
            onChange={(_, value) => setSelectedBrand(value ?? '')}
            disabled={submitting}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="Refrigerator brand"
                placeholder="e.g. Samsung, LG, Whirlpool"
                size="small"
              />
            )}
          />

          {/* Model picker — options come from the static FILTER_MODELS lookup
              keyed by brand. `freeSolo` lets the user submit a model that
              isn't on the list (the backend match endpoint owns the final
              decision and 404s otherwise). `onInputChange` keeps `model`
              state in sync while the user is typing rather than only when
              they pick an option from the dropdown. */}
          <Autocomplete
            options={selectedBrand ? FILTER_MODELS[selectedBrand] ?? [] : []}
            value={model || null}
            onChange={(_, value) => setModel(value ?? '')}
            onInputChange={(_, value) => setModel(value)}
            freeSolo
            disabled={submitting || !selectedBrand}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Model number"
                placeholder="e.g. RF268ABRS"
              />
            )}
          />

          <Button
            variant="contained"
            onClick={handleCheck}
            disabled={submitting || !selectedBrand || !model.trim()}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? 'Checking…' : 'Find my filter'}
          </Button>
        </Stack>
      </Collapse>

      {/* ── Match result ────────────────────────────────────────────── */}
      <Collapse in={step === 'result' && !!filterCode} unmountOnExit>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleOutlineIcon color="success" />
            <Typography variant="h5" color="success.main">
              Compatible
            </Typography>
          </Stack>
          <Typography variant="body2">
            Your {matchedBrand} {matchedModel} is compatible with the SIPP filter.
            Replace quarterly for best results.
          </Typography>
          <Box
            sx={{
              border: `1px dashed ${theme.palette.grey[400]}`,
              borderRadius: 1.5,
              p: 1.5,
              backgroundColor: theme.palette.grey[50],
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Your SIPP filter code
            </Typography>
            <Typography
              sx={{
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              {filterCode}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => window.open(STRIPE_PRO_ANNUAL_URL, '_blank')}
          >
            Get SIPP Pro — $99/year
          </Button>
          <Link
            component="button"
            type="button"
            onClick={resetToSelect}
            sx={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              textTransform: 'none',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
            Edit refrigerator
          </Link>
        </Stack>
      </Collapse>

      {/* ── No match → research request ─────────────────────────────── */}
      <Collapse in={step === 'research'} unmountOnExit>
        <Stack spacing={2}>
          <Typography variant="h5">We don't have that model yet</Typography>
          <Typography variant="body2" color="text.secondary">
            We don't have a verified filter match for {selectedBrand} {model}
            {' '}yet — we'll notify you when we do.
          </Typography>

          {researchSubmitted ? (
            <Box
              sx={{
                border: `1px solid ${theme.palette.success.light}`,
                backgroundColor: theme.palette.success.light,
                borderRadius: 1.5,
                p: 2,
              }}
            >
              <Typography variant="body2" color="success.dark" fontWeight={600}>
                Thanks — we'll be in touch.
              </Typography>
              <Typography variant="caption" color="success.dark">
                We'll email {researchEmail} once {selectedBrand} {model} is
                supported.
              </Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={researchEmail}
                onChange={(e) => {
                  setResearchEmail(e.target.value)
                  if (researchError) setResearchError('')
                }}
                error={!!researchError}
                helperText={researchError}
                size="small"
              />
              <Stack direction="row" spacing={1.5}>
                <Button variant="contained" onClick={handleResearchSubmit}>
                  Research my model
                </Button>
                <Button
                  variant="text"
                  onClick={resetToSelect}
                  sx={{ textTransform: 'none' }}
                >
                  ← Try a different model
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Collapse>
    </Stack>
  )
}
